'use client'
import type { ChangeEvent, FC } from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { NumberFormatProps, NumberFormatValues } from 'react-number-format'
import NumberFormat from 'react-number-format'
import type { BankDetails } from './constants'
import {
  formatCreditCard,
  getBankDetailsFromCardNumber,
  persianNumbersToEnglish,
} from './utils'
import { CenterBox, Icon, Input, type InputProps } from '@repo/ui/components'

interface BankCardFieldProps extends NumberFormatProps {
  maskCharacter?: string
  separatorCharacter?: string
}

export type { BankCardFieldProps }

const CustomTextField = (props: InputProps) => {
  return (
    <Input
      {...props}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        e.target.value = persianNumbersToEnglish(e.target.value)
        props.onChange?.(e)
      }}
      autoComplete='off'
      autoFocus
    />
  )
}

export const BankCardField: FC<BankCardFieldProps> = forwardRef<
  HTMLInputElement,
  BankCardFieldProps
>(function BankCardField(
  {
    InputProps: InputPropsProp,
    onValueChange,
    maskCharacter = '',
    separatorCharacter,
    label = 'شماره کارت',
    ...rest
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ref,
) {
  const [bankDetails, setBankDetails] = useState<BankDetails>()
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current && rest.value) {
      const formattedValue = (
        typeof rest.value === 'string' ? rest.value : rest.value.toString()
      ).replace(/-/g, '')
      setBankDetails(getBankDetailsFromCardNumber(formattedValue))
      isFirstRender.current = false
    }
  }, [rest.value])

  const InputProps = useMemo(
    () => ({
      className: 'ltr',
      icon: (
        <CenterBox>
          {bankDetails?.icon ?? <Icon name='CardGhost' size='sm' />}
        </CenterBox>
      ),
      ...InputPropsProp,
    }),
    [bankDetails, InputPropsProp],
  )

  const valueChangeHandler = useCallback(
    (data: NumberFormatValues) => {
      setBankDetails(getBankDetailsFromCardNumber(data.value))
      onValueChange?.(data)
    },
    [onValueChange],
  )

  const customFormat = useCallback(
    (data: string): string => {
      let val = data

      if (data?.includes('-')) {
        val = data.replace(/-/g, '')
      }

      if (val?.length > 16) {
        val = data.substring(0, 16)
      }
      return formatCreditCard(val, maskCharacter, separatorCharacter)
    },
    [maskCharacter, separatorCharacter],
  )

  const removedNoneNumberChars = useCallback(
    (formattedValue: string): string => {
      const englishNumbers = persianNumbersToEnglish(formattedValue)
      return englishNumbers?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '')
    },
    [],
  )
  return (
    <NumberFormat
      allowEmptyFormatting
      customInput={CustomTextField}
      data-testid='bank-card-field'
      format={customFormat}
      {...InputProps}
      isNumericString={true}
      label={label}
      removeFormatting={removedNoneNumberChars}
      type='tel'
      onValueChange={valueChangeHandler}
      {...rest}
      className='text-center rtl:pl-xl'
    />
  )
})
